import '@uiw/react-md-editor/markdown-editor.css';

import { useCallback } from 'react';
import MDEditor, { commands } from '@uiw/react-md-editor';
import { cx } from '@trinity/chat-components/utils';
import styles from './style/InlineMarkdownEditor.module.css';

// Custom toolbar commands - simplified without title options
const toolbarCommands = [
  commands.bold,
  commands.italic,
  commands.strikethrough,
  commands.hr,
  commands.divider,
  commands.link,
  commands.quote,
  commands.code,
  commands.image,
  commands.divider,
  commands.unorderedListCommand,
  commands.orderedListCommand,
  commands.checkedListCommand,
];

type Props = {
  /**
   * The current markdown value
   */
  value: string;

  /**
   * Callback fired when the markdown content changes
   */
  onChange: (value: string) => void;

  /**
   * Callback fired when the user clicks Save
   */
  onSave?: () => void;

  /**
   * Callback fired when the user clicks Cancel
   */
  onCancel?: () => void;

  /**
   * Whether there are unsaved changes (enables Save button)
   */
  hasChanges?: boolean;

  /**
   * Whether a save operation is in progress
   */
  isSaving?: boolean;

  /**
   * Whether the editor is disabled/read-only
   */
  disabled?: boolean;

  /**
   * Placeholder text when the editor is empty
   */
  placeholder?: string;

  /**
   * Custom label for the Save button
   * @default "Save"
   */
  saveLabel?: string;

  /**
   * Custom label for the Cancel button
   * @default "Cancel"
   */
  cancelLabel?: string;

  /**
   * Height of the editor
   * @default 300
   */
  height?: number;

  /**
   * Whether to show the action buttons (Save/Cancel)
   * @default true
   */
  showActions?: boolean;

  /**
   * Optional custom class names
   */
  classes?: {
    root?: string;
    editor?: string;
    buttons?: string;
  };
};

/**
 * An inline Markdown Editor component for embedding directly in a page.
 * This is a controlled component that wraps @uiw/react-md-editor.
 * 
 * Unlike the modal MarkdownEditor, this component is designed to be
 * embedded directly in a form or page layout.
 * 
 * @example
 * ```tsx
 * <InlineMarkdownEditor
 *   value={persona}
 *   onChange={setPersona}
 *   onSave={handleSavePersona}
 *   onCancel={handleCancelPersona}
 *   hasChanges={hasPersonaChanges}
 *   isSaving={updatePersonaMutation.isPending}
 *   placeholder="Paste or type your persona details here..."
 * />
 * ```
 */
export const InlineMarkdownEditor = ({
  value,
  onChange,
  onSave,
  onCancel,
  hasChanges = false,
  isSaving = false,
  disabled = false,
  placeholder = 'Type your content here...',
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  height = 300,
  showActions = true,
  classes = {},
}: Props) => {
  const handleChange = useCallback((newValue: string | undefined) => {
    onChange(newValue || '');
  }, [onChange]);

  return (
    <div className={cx(styles.root, classes.root)} data-color-mode="light">
      <div className={cx(styles.editorContainer, classes.editor)}>
        <MDEditor
          value={value}
          onChange={handleChange}
          height={height}
          preview="edit"
          hideToolbar={false}
          commands={toolbarCommands}
          extraCommands={[]}
          visibleDragbar={false}
          textareaProps={{
            placeholder,
            disabled,
          }}
        />
      </div>
      {showActions && (onSave || onCancel) && (
        <div className={cx(styles.buttons, classes.buttons)}>
          {onSave && (
            <button
              type="button"
              className={cx(styles.button, styles.buttonPrimary)}
              disabled={!hasChanges || isSaving || disabled}
              onClick={onSave}>
              {isSaving ? (
                <span className={styles.loadingSpinner} />
              ) : null}
              {saveLabel}
            </button>
          )}
          {onCancel && (
            <button
              type="button"
              className={cx(styles.button, styles.buttonSecondary)}
              disabled={!hasChanges || isSaving}
              onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

InlineMarkdownEditor.displayName = 'InlineMarkdownEditor';
